import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import { tKey } from 'ember-intl';
import type { GeneralDatepickersDatepickerWithSidebarSignature } from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import type { MetricType } from 'lumen/models/market-performance/data-item';
import type MarketSnapshotsOverviewRoute from 'lumen/routes/market/snapshots/overview';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { LOCAL_STORAGE_KEYS } from 'lumen/services/local-storage';
import {
  type DataPreferences,
  DEFAULT_DATA_PREFERENCES,
  DEFAULT_TABLE_LAYOUT_PREFERENCES,
  remapOrderByMetric,
  type TableLayoutPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';
import type { ModelFrom } from 'lumen/utils/routes';

export default class MarketSnapshotsOverviewController extends Controller {
  declare model: ModelFrom<MarketSnapshotsOverviewRoute>;

  queryParams = ['fromDate', 'toDate', 'orderBy', 'page', 'pageSize'];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service('local-storage') declare localStorage: Services['local-storage'];

  @tracked dataPreferences: DataPreferences = DEFAULT_DATA_PREFERENCES;
  @tracked fromDate?: string;
  // Format: "field" for ascending, "-field" for descending (e.g. "otb.revenue", "-otb.revenue")
  @tracked orderBy = 'name';
  @tracked page = 1;
  @tracked pageSize = 25;
  @tracked tableLayoutPreferences: TableLayoutPreferences =
    DEFAULT_TABLE_LAYOUT_PREFERENCES;
  @tracked toDate?: string;

  pageSizeOptions = [25, 50, 100, 250];

  get currentPageSizeOption(): { label: string; value: number } {
    return {
      value: this.pageSize,
      label: this.intl.t('general.page_no_of_rows', {
        noOfRows: this.intl.formatNumber(this.pageSize),
      }),
    };
  }

  get datepickerSidebarLists(): GeneralDatepickersDatepickerWithSidebarSignature['Args']['sidebarLists'] {
    const today = this.hotelAndMarketService.getLocalTime();

    return [
      {
        title: this.intl.t('general.selected_period'),
        options: [
          {
            id: 'today',
            text: tKey('general.today'),
            range: { start: today, end: today },
          },
          {
            id: 'yesterday',
            text: tKey('general.yesterday'),
            range: {
              start: today.subtract(1, 'day'),
              end: today.subtract(1, 'day'),
            },
          },
          {
            id: 'this-week',
            text: tKey('general.this_week'),
            range: { start: today.startOf('week'), end: today.endOf('week') },
          },
          {
            id: 'this-month',
            text: tKey('general.this_month'),
            range: { start: today.startOf('month'), end: today.endOf('month') },
          },
          {
            id: 'this-year',
            text: tKey('general.this_year'),
            range: { start: today.startOf('year'), end: today.endOf('year') },
          },
          {
            id: 'custom',
            text: tKey('general.custom'),
            range: { start: today.startOf('month'), end: today.endOf('month') },
          },
        ],
      },
    ];
  }

  get dateRange(): { end: Dayjs; start: Dayjs } {
    const today = this.hotelAndMarketService.getLocalTime();

    return {
      start: this.fromDate
        ? this.hotelAndMarketService.toLocalTime(this.fromDate)
        : today.startOf('month'),
      end: this.toDate
        ? this.hotelAndMarketService.toLocalTime(this.toDate)
        : today.endOf('month'),
    };
  }

  get maxDate(): Dayjs {
    const today = this.hotelAndMarketService.getLocalTime();
    return today.add(2, 'year').endOf('year');
  }

  get showingText(): string {
    const totalRecords = this.totalRecords ?? 0;

    if (totalRecords === 0) {
      return '';
    }

    const startIndex = (this.page - 1) * this.pageSize + 1;
    const endIndex = Math.min(this.page * this.pageSize, totalRecords);

    return this.intl.t('general.page_showing', {
      currentPage: this.intl.formatNumber(startIndex),
      lastPage: this.intl.formatNumber(endIndex),
      totalRecords: this.intl.formatNumber(totalRecords),
    });
  }

  get totalRecords(): number | null {
    const metaCount =
      this.model.loadDataItemsTask.lastSuccessful?.value?.meta?.count;

    if (metaCount === undefined) {
      return null;
    }

    return metaCount;
  }

  get visibleMetricTypes(): MetricType[] {
    return [
      'otb',
      this.dataPreferences.sdlyColumn,
      'ly',
      this.dataPreferences.forecastColumn,
      'budget',
    ];
  }

  @action
  onChangePageSize(pageSizeOption: { label: string; value: number }): void {
    this.pageSize = pageSizeOption.value;
    this.page = 1;
  }

  @action
  onSort(orderBy: string): void {
    this.orderBy = orderBy;
    this.page = 1;
  }

  @action
  setPage(page: number): void {
    this.page = page;
  }

  @action
  updateDataPreferences(preferences: DataPreferences): void {
    // sdlyColumn and forecast affect the API request; variance is applied client-side.
    const sdlyColumnChanged =
      preferences.sdlyColumn !== this.dataPreferences.sdlyColumn;
    const forecastChanged =
      preferences.forecastColumn !== this.dataPreferences.forecastColumn;

    // A sort on a swapped column would otherwise be left pointing at a metric
    // that's no longer requested; repoint it to the equivalent new column.
    let orderBy = this.orderBy;
    if (sdlyColumnChanged) {
      orderBy = remapOrderByMetric(
        orderBy,
        this.dataPreferences.sdlyColumn,
        preferences.sdlyColumn,
      );
    }
    if (forecastChanged) {
      orderBy = remapOrderByMetric(
        orderBy,
        this.dataPreferences.forecastColumn,
        preferences.forecastColumn,
      );
    }

    this.dataPreferences = preferences;
    this.localStorage.setItem(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_DATA_PREFERENCES,
      JSON.stringify(preferences),
    );

    if (orderBy !== this.orderBy) {
      // Setting orderBy (a refreshModel QP) refreshes the model on its own;
      // also calling doRefresh would abort that transition and revert orderBy.
      this.orderBy = orderBy;
      this.page = 1;
    } else if (sdlyColumnChanged || forecastChanged) {
      this.send('doRefresh');
    }
  }

  @action
  updateDateRange(range: { end: Dayjs | null; start: Dayjs }): void {
    const fromDate = range.start.format('YYYY-MM-DD');
    const toDate = (range.end ?? range.start).format('YYYY-MM-DD');

    this.fromDate = fromDate;
    this.toDate = toDate;
  }

  @action
  updateTableLayoutPreferences(preferences: TableLayoutPreferences): void {
    this.tableLayoutPreferences = preferences;
    this.localStorage.setItem(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_TABLE_LAYOUT,
      JSON.stringify(preferences),
    );
  }
}
